import { API_BASE } from '../config';

export const api = {
  async request(endpoint, options = {}) {
    const token = localStorage.getItem('token');
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    };

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || 'Request failed');
    }

    return response.json();
  },

  // HTTP method helpers
  async get(endpoint) {
    const data = await this.request(endpoint);
    return { data };
  },

  async post(endpoint, body) {
    const data = await this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return { data };
  },

  async put(endpoint, body) {
    const data = await this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
    return { data };
  },

  // Auth
  login: (email, password) =>
    api.request('/auth/dealer/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  register: (data) =>
    api.request('/dealers/register', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Dealer
  getProfile: () => api.request('/dealers/me'),

  updateProfile: (data) =>
    api.request('/dealers/me', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  acceptAgreement: (version) =>
    api.request('/dealers/me/agreement', {
      method: 'POST',
      body: JSON.stringify({ agreement_version: version, accepted: true }),
    }),

  updateLicensing: (data) =>
    api.request('/dealers/me/licensing', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  getStats: () => api.request('/dealers/me/stats'),

  // Partner
  partnerLogin: (email, password) =>
    api.request('/partners/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  partnerRegister: (data) =>
    api.request('/partners/register', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getPartnerProfile: () => api.request('/partners/me'),

  getAvailableDealers: () => api.request('/partners/dealers'),

  getSubscriptions: () => api.request('/partners/subscriptions'),

  subscribe: (dealershipId) =>
    api.request('/partners/subscriptions', {
      method: 'POST',
      body: JSON.stringify({ dealership_id: dealershipId }),
    }),

  unsubscribe: (subscriptionId) =>
    api.request(`/partners/subscriptions/${subscriptionId}`, {
      method: 'DELETE',
    }),

  getDealerInventory: (dealershipId) =>
    api.request(`/partners/inventory/${dealershipId}`),

  // Onboarding
  validateSetupToken: (token) =>
    api.request(`/onboarding/setup/${token}`),

  activateAccount: (token, email, password) =>
    api.request('/onboarding/activate', {
      method: 'POST',
      body: JSON.stringify({ token, email, password }),
    }),

  // ===== Photographer asset portal =====
  // Multipart upload helper — must NOT set Content-Type (browser sets the
  // multipart boundary). The /api/photographer/ prefix is exempt from X-API-Key,
  // so Bearer is the only header we need.
  async requestForm(endpoint, formData, method = 'POST') {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_BASE}${endpoint}`, {
      method,
      headers: { ...(token && { Authorization: `Bearer ${token}` }) },
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(error.detail || 'Upload failed');
    }
    return response.json();
  },

  photographerLogin: (email, password) =>
    api.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  getAssignedDealerships: (q = '') =>
    api.request(`/photographer/assets/dealerships?q=${encodeURIComponent(q)}`),

  // Admin: list of photographers for the by-photographer dropdown.
  getPhotographers: () => api.request('/photographer/photographers'),

  // Vehicles a photographer has shot, newest-first. Photographers always get
  // their own (param ignored server-side); admins pass a photographer_id.
  getShotVehicles: (photographerId) =>
    api.request(`/photographer/assets/shot-vehicles${photographerId ? `?photographer_id=${photographerId}` : ''}`),

  // Cross-dealership search (assigned dealers for photographers, all for admins).
  // Matches stock #, VIN, year/make/model, and dealership name.
  searchAllVehicles: (q, limit = 60) =>
    api.request(`/photographer/assets/vehicles/search?${new URLSearchParams({ q, limit })}`),

  getPhotographerVehicles: (dealerId, params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    ).toString();
    return api.request(`/photographer/assets/dealerships/${dealerId}/vehicles${qs ? `?${qs}` : ''}`);
  },

  getPhotographerVehicle: (dealerId, vin) =>
    api.request(`/photographer/assets/dealerships/${dealerId}/vehicles/${vin}`),

  updatePhotographerVehicle: (dealerId, vin, fields) =>
    api.request(`/photographer/assets/dealerships/${dealerId}/vehicles/${vin}/update`, {
      method: 'PUT',
      body: JSON.stringify({ fields }),
    }),

  reorderPhotographerPhotos: (dealerId, vin, order) =>
    api.request(`/photographer/assets/dealerships/${dealerId}/vehicles/${vin}/photos/order`, {
      method: 'PUT',
      body: JSON.stringify({ order }),
    }),

  deletePhotographerPhoto: (dealerId, vin, mediaId) =>
    api.request(`/photographer/assets/dealerships/${dealerId}/vehicles/${vin}/photos/by-id/${mediaId}`, {
      method: 'DELETE',
    }),

  deletePhotographerVideo: (dealerId, vin, mediaId) =>
    api.request(`/photographer/assets/dealerships/${dealerId}/vehicles/${vin}/videos/by-id/${mediaId}`, {
      method: 'DELETE',
    }),

  uploadPhotographerPhoto: (dealerId, vin, file, shotName) => {
    const fd = new FormData();
    fd.append('file', file);
    if (shotName) fd.append('shot_name', shotName);
    return api.requestForm(`/photographer/assets/dealerships/${dealerId}/vehicles/${vin}/photos`, fd);
  },

  uploadPhotographerVideo: (dealerId, vin, file, videoType) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('video_type', videoType || 'walkaround');
    return api.requestForm(`/photographer/assets/dealerships/${dealerId}/vehicles/${vin}/videos`, fd);
  },

  // "Can't shoot" status — set a reason (blocks the vehicle) or clear it.
  setShootStatus: (dealerId, vin, reason) =>
    api.request(`/photographer/assets/dealerships/${dealerId}/vehicles/${vin}/shoot-status`, {
      method: 'PUT',
      body: JSON.stringify({ reason }),
    }),

  clearShootStatus: (dealerId, vin) =>
    api.request(`/photographer/assets/dealerships/${dealerId}/vehicles/${vin}/shoot-status`, {
      method: 'DELETE',
    }),
};

// Decode the `role` claim from a JWT without a library (base64url payload).
export function decodeJwtRole(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return { role: payload.role, user_id: payload.user_id, email: payload.email };
  } catch (e) {
    return {};
  }
}

// Default export for compatibility
export default api;
